import { useContext, useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Layout } from "../../../../containers/layout/dashboard";
import { UserRoleWrapper } from "./styled";
import { BaseButton } from "../../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { BaseFieldSet } from "../../../../components/formfields/fieldset/styled";
import { BaseLabel } from "../../../../components/formfields/label/styled";
import { SearchIcon } from "../../../../assets";
import { Context } from "../../../../context";
import { BaseFormModal } from "../../../../components/modals/form";
import { BaseConfirmationModal } from "../../../../components/modals/confirmation";
import { BaseFormModalRefType } from "../../../../types/app.type";
import { createRoleService } from "../../../../utils/apis/usermanagement/role/createRole";
import Cookies from "universal-cookie";
import { retrieveAllRolesService } from "../../../../utils/apis/usermanagement/role/retrieveAllRoles";
import { BaseTable } from "../../../../components/table";
import { retrieveRoleByIdService } from "../../../../utils/apis/usermanagement/role/retrieveRoleById";
import { BaseAlertModal } from "../../../../components/modals/alert";
import { updateRoleService } from "../../../../utils/apis/usermanagement/role/updateRole";
import { deleteRoleService } from "../../../../utils/apis/usermanagement/role/deleteRole";
import { BaseInput } from "../../../../components/formfields/input/styled";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

export const UserRole = () => {
	const voidRole = "MFI Admin";
	const headers = ["Name", "Job Description", "Action"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(Context);
	const formModalRef = useRef<BaseFormModalRefType>(null);

	const [isLoading, setIsLoading] = useState(false);
	const [action, setAction] = useState("create");
	const [openAlertModal, setOpenAlertModal] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedRole, setSelectedRole] = useState({
		id: "",
		title: "",
		description: "",
		accessControl: "",
	});
	const [filter, setFilter] = useState<string>("");
	const [isFormModalOpen, setIsFormModalOpen] = useState(false);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

	useEffect(() => {
		setActiveNavItem("User Management");
	}, [setActiveNavItem]);

	const { data: filteredRoles, refetch: refetchFilteredRoles } = useQuery({
		queryKey: ["filteredRoles", TOKEN, filter],
		queryFn: () => retrieveAllRolesService(filter, TOKEN),
		enabled: !!TOKEN,
	});

	useEffect(() => {
		if (openAlertModal) {
			refetchFilteredRoles();
		}
	}, [openAlertModal, refetchFilteredRoles]);

	// form modal controls
	const handleOpenFormModal = () => {
		setAction("create");
		formModalRef.current?.setRoleDetails({ title: "", description: "" });
		return setIsFormModalOpen(true);
	};

	const handleFormModalPersist = () => {
		if (isLoading) return;
		// reset error to default
		setError(null);
		// reset selected role to initial value
		setSelectedRole({
			id: "",
			title: "",
			description: "",
			accessControl: "",
		});
		return setIsFormModalOpen(false);
	};

	// alert modal controls
	const handleAlertModalPersist = () => {
		return setOpenAlertModal(true);
	};

	const handleCloseAlertModal = () => {
		// reset action to default
		setAction("create");
		// reset selected role to initial value
		setSelectedRole({
			id: "",
			title: "",
			description: "",
			accessControl: "",
		});
		return setOpenAlertModal(false);
	};

	// confirmation modal controls
	const handleCancelClick = () => {
		// reset error to default
		setError(null);
		// reset action to default
		setAction("create");
		// reset selected role to initial value
		setSelectedRole({
			id: "",
			title: "",
			description: "",
			accessControl: "",
		});
		return setIsConfirmationModalOpen(false);
	};

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const value = e.target.value;
		setFilter(value);
	};

	const retrieveUserRole = async (id: string) => {
		try {
			const data = await retrieveRoleByIdService(id, TOKEN);
			setSelectedRole(data);
			return data;
		} catch (err) {
			console.error("Failed to fetch user role:", err);
			return { title: "", description: "" };
		}
	};

	const handleCallToActionClick = async (
		e:
			| React.MouseEvent<HTMLButtonElement, MouseEvent>
			| React.MouseEvent<HTMLSpanElement, MouseEvent>,
		id: string,
		action?: string
	) => {
		setAction(action ?? "create");
		const role = await retrieveUserRole(id);
		if (action === "edit") {
			formModalRef.current?.setRoleDetails(role);
			setIsFormModalOpen(true);
		}
		if (action === "delete") {
			setIsConfirmationModalOpen(true);
		}
	};

	const handleNavigateToAccessControl = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		e.preventDefault();
		// reset error to default
		setError(null);
		//
		if (selectedRole.title === voidRole) return;
		return navigate(
			`/usermanagement/accesscontrol${
				selectedRole.accessControl && `/${selectedRole.accessControl}`
			}`
		);
	};

	const handleRoleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		const payload = formModalRef.current?.getRoleDetails();
		try {
			let response;
			switch (action) {
				case "create":
					response = await createRoleService(payload, TOKEN);
					break;
				case "edit":
					response = await updateRoleService(payload, selectedRole.id, TOKEN);
					break;
				default:
					response = await createRoleService(payload, TOKEN);
					break;
			}
			if (response.status === "success") {
				setIsLoading(false);
				formModalRef.current?.clearRoleDetails();
				setIsFormModalOpen(false);
				setOpenAlertModal(true);
			} else {
				setIsLoading(false);
				// reset selected role to initial value
				setSelectedRole({
					id: "",
					title: "",
					description: "",
					accessControl: "",
				});
				setError(
					"Role creation failed. Please check your credentials and try again."
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Role creation failed. ${error.message}`);
			console.error("Role creation failed:", error);
		}
	};

	const handleDeleteUserRole = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		try {
			const response = await deleteRoleService(selectedRole.id, TOKEN);
			if (response.status === "success") {
				setIsLoading(false);
				// reset selected role to initial value
				setSelectedRole({
					id: "",
					title: "",
					description: "",
					accessControl: "",
				});
				setIsConfirmationModalOpen(false);
				refetchFilteredRoles();
			} else {
				setIsLoading(false);
				setError("Role deletion failed. Please try again.");
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Role deletion failed. ${error.message}`);
			console.error("Role deletion failed:", error);
		}
	};

	return (
		<Layout id="user-role">
			<UserRoleWrapper>
				<BaseAlertModal
					title="Success"
					message={
						action === "create"
							? "User role successfully created"
							: "User role successfully updated"
					}
					open={openAlertModal}
					handleClose={handleAlertModalPersist}
					callToAction="Continue"
					handleCallToAction={handleCloseAlertModal}
				/>
				<BaseFormModal
					ref={formModalRef}
					title={action === "create" ? "Add Role" : "Update Role"}
					location="userrole"
					error={error ?? ""}
					isLoading={isLoading}
					open={isFormModalOpen}
					callToAction={action === "create" ? "Save" : "Update"}
					className="role-form-modal"
					handleClose={handleFormModalPersist}
					handleSubmit={handleRoleSubmit}
					secondaryCallToAction={
						!(action === "create" || selectedRole.title === "MFI Admin")
							? "Edit Access"
							: undefined
					}
					handleSecondaryCTAClick={handleNavigateToAccessControl}
				/>
				<BaseConfirmationModal
					error={error}
					isLoading={isLoading}
					open={isConfirmationModalOpen}
					className="role-confirmation-modal"
					body="Once deleted, you will not be able to recover this information!"
					handleClose={handleConfirmationModalPersist}
					handleConfirm={handleDeleteUserRole}
					handleCancel={handleCancelClick}
				/>
				<Stack
					gap={{
						mobile: "calc(var(--flex-gap)/2)",
						miniTablet: "var(--flex-gap)",
					}}
					alignItems={{ miniTablet: "center" }}
					justifyContent={"space-between"}
					direction={{ miniTablet: "row" }}
					overflow={"hidden"}
				>
					<Box>
						<Typography
							variant="h1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={24}
							lineHeight={"normal"}
							color={"#373737"}
						>
							User Roles
						</Typography>
					</Box>
					<Box>
						<BaseButton
							variant="contained"
							radius="7px"
							fontSize={16}
							fontWeight={800}
							bgcolor="#791377"
							startIcon={<AddIcon />}
							padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
							sx={{
								width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							}}
							onClick={handleOpenFormModal}
						>
							<Typography
								variant={"button"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
								textTransform={"inherit"}
							>
								Add Role
							</Typography>
						</BaseButton>
					</Box>
				</Stack>
				<Box
					marginBlock={"calc(var(--basic-margin)/2) calc(var(--basic-margin))"}
				>
					<form>
						<BaseFieldSet>
							<BaseLabel colour="#505050" fontWeight={500} fontSize="15px">
								Select filter
							</BaseLabel>
							<BaseInput
								name="key"
								colour="#666666"
								bgcolor="#FFFFFF"
								fontWeight={400}
								value={filter}
								placeholder="Enter Role"
								onChange={(e) => handleChange(e)}
								startAdornment={
									<Box component={"div"} className="search-icon-box">
										<SearchIcon />
									</Box>
								}
							/>
						</BaseFieldSet>
					</form>
				</Box>
				<BaseTable
					headers={headers}
					rows={filteredRoles}
					location="all-user-roles"
					handleItemClick={handleCallToActionClick}
				/>
			</UserRoleWrapper>
		</Layout>
	);
};
